import c from 'classnames';
import { IconChevronDown, IconX } from 'featherico';
import type * as React from 'react';
import type { ChangeEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useAsync } from 'react-async-hook';
import { Manager, Popper, Reference } from 'react-popper';
import api from '../../../../api';
import type { MinuteStepSizeDto } from '../../../features/Admin/CompanySettings/types';
import ClickOutside from '../../ClickOutside';
import { InputButtonIcon } from '../../Input/InputButton';
import { InputIcon } from '../../Input/InputIcon';
import type { HourFormat } from './TimePickerPopup';
import TimePickerPopup from './TimePickerPopup';
import { format12Hour, isValidTime, toValidTime } from './TimePickerPopup/time';
import styles from './style.m.less';

const EMPTY_VALUE = '';

const KINDS_CLASSMAP = {
    DEFAULT: c(styles.kindDefault),
    TITLE: c(styles.kindTitle),
};

interface Props {
    id?: string;
    kind: keyof typeof KINDS_CLASSMAP;
    format?: HourFormat;
    timeZone: string;
    value?: string;
    onChange: (time: string | undefined) => void;
    stepSizeMins?: MinuteStepSizeDto;
    isClearable?: boolean;
    hasError?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    placeholder?: string;
    showIcon?: boolean;
    centerText?: boolean;
    dataIntercomTarget?: string;
}

export default function TimePicker({
    id,
    kind,
    format = '24hr',
    timeZone,
    value: initialValue,
    onChange,
    stepSizeMins,
    isClearable = false,
    hasError: initialHasError = false,
    disabled = false,
    readOnly = false,
    placeholder = 'Select time',
    showIcon = true,
    centerText = false,
    dataIntercomTarget,
}: Props) {
    const [value, setValue] = useState<string>(initialValue || EMPTY_VALUE);
    const [inputValue, setInputValue] = useState<string>(format === '12hr' ? format12Hour(value) : value);
    const [hasError, setHasError] = useState(initialHasError);
    const { result: fetchedStepSizeMins } = useAsync(
        () =>
            stepSizeMins === undefined
                ? api.companyDateTimeSettings.getDateTimeSettings().then(res => res.minuteStepSize)
                : Promise.resolve(undefined),
        [stepSizeMins]
    );
    const usedStepSizeMins: MinuteStepSizeDto = fetchedStepSizeMins ?? stepSizeMins ?? 15;

    const handleValueChange = (newValue: string) => {
        if (!isValidTime(newValue, isClearable)) {
            setHasError(true);
            return;
        }

        setHasError(false);
        setValue(newValue);

        if (isClearable && newValue === EMPTY_VALUE) {
            onChange(undefined);
        } else {
            onChange(toValidTime(newValue));
        }
    };

    useEffect(() => {
        if (initialValue && initialValue !== value) {
            setInputValue(format === '12hr' ? format12Hour(initialValue) : initialValue);
            setValue(initialValue);
            setHasError(false);
        }
    }, [initialValue]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setHasError(initialHasError);
    }, [initialHasError]);

    const [isOpen, setOpen] = useState(false);
    const [skipBlur, setSkipBlur] = useState(false);
    const [isFocussed, setIsFocussed] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);

    const handleInputClick = () => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
        setIsFocussed(true);
        setOpen(true);
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.currentTarget.value;
        setValue(newValue);
        setInputValue(newValue);
        handleValueChange(newValue);
    };

    const handlePickerChange = (newValue: string) => {
        setValue(newValue);
        setInputValue(format === '12hr' ? format12Hour(newValue) : newValue);
        setHasError(false);
        handleValueChange(newValue);
    };

    const closePicker = () => setOpen(false);
    const clearValue = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setInputValue(EMPTY_VALUE);
        handleValueChange(EMPTY_VALUE);
        closePicker();
    };

    const handlePopupMouseDown = () => setSkipBlur(true);
    const handleBlur = () => {
        setIsFocussed(false);

        if (skipBlur) {
            setSkipBlur(false);
            return;
        }

        closePicker();
    };
    const handleFocus = () => {
        setIsFocussed(true);
        setOpen(true);
    };
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            closePicker();
        }
    };

    const isInteractive = !disabled && !readOnly;
    const showClearButton = showIcon && isClearable && isInteractive && value !== EMPTY_VALUE;
    return (
        <div
            onBlur={handleBlur}
            className={c(KINDS_CLASSMAP[kind], styles.wrapper, {
                [styles.twelveHour]: format === '12hr',
                [styles.twentyFourHour]: format === '24hr',
                [styles.alignCenter]: centerText,
            })}
        >
            <ClickOutside onClickOutside={closePicker} enabled={isOpen}>
                <Manager>
                    <Reference>
                        {({ ref }) => (
                            <>
                                <div
                                    ref={ref}
                                    className={c(styles.box, {
                                        [styles.withIcon]: showIcon,
                                        [styles.invalid]: hasError,
                                        [styles.disabled]: disabled,
                                        [styles.readOnly]: readOnly,
                                        [styles.focus]: isFocussed,
                                    })}
                                    onClick={isInteractive ? handleInputClick : undefined}
                                    role="textbox"
                                >
                                    <input
                                        id={id}
                                        type="text"
                                        value={inputValue}
                                        className={styles.input}
                                        onChange={handleInputChange}
                                        onFocus={isInteractive ? handleFocus : undefined}
                                        placeholder={placeholder}
                                        ref={inputRef}
                                        aria-invalid={hasError}
                                        aria-label="Time in format hour hour colon minute minute"
                                        disabled={disabled}
                                        readOnly={readOnly}
                                        onKeyDown={handleKeyDown}
                                        maxLength={format === '24hr' ? 5 : 8}
                                        data-intercom-target={dataIntercomTarget}
                                    />

                                    {showIcon && (
                                        <InputIcon
                                            small
                                            className={c(styles.chevron, {
                                                [styles.hideOnHover]: showClearButton,
                                                [styles.disabled]: disabled,
                                            })}
                                            aria-hidden={true}
                                        >
                                            <IconChevronDown />
                                        </InputIcon>
                                    )}
                                </div>

                                {showClearButton && (
                                    <InputButtonIcon
                                        className={styles.clearButton}
                                        onClick={clearValue}
                                        size={kind}
                                        aria-label="Clear Time"
                                    >
                                        <IconX />
                                    </InputButtonIcon>
                                )}
                            </>
                        )}
                    </Reference>

                    {isOpen && (
                        <Popper placement="bottom-start">
                            {({ ref, style }) => (
                                <div ref={ref} style={style} className={styles.popup}>
                                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                                    <div
                                        className={c(styles.selectorWrapper, {
                                            [styles.format12Hr]: format === '12hr',
                                        })}
                                        onMouseDown={handlePopupMouseDown}
                                    >
                                        <TimePickerPopup
                                            timeZone={timeZone}
                                            format={format}
                                            value={value}
                                            onChange={handlePickerChange}
                                            stepSizeMins={usedStepSizeMins}
                                        />
                                    </div>
                                </div>
                            )}
                        </Popper>
                    )}
                </Manager>
            </ClickOutside>
        </div>
    );
}
